import { NgModule, APP_INITIALIZER, LOCALE_ID, PLATFORM_ID, provideZonelessChangeDetection, provideEnvironmentInitializer } from '@angular/core';
import { BrowserModule, Meta, provideClientHydration, withEventReplay, withNoHttpTransferCache, withIncrementalHydration } from '@angular/platform-browser';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { BehaviorSubject } from 'rxjs';
import { ADS_PARAMS } from '@app/ads/tokens';
import { AppComponent } from './app.component';
import { LayoutsModule } from './layouts/layouts.module';
import { AppRoutingModule } from './app-routing.module';
import { CustomeUrlSerializer } from '@app/core/services/CustomeUrlSerializer';
import { UrlSerializer } from '@angular/router';
import { configurePopovers } from '@app/popover/popover.config';
import { configureModals } from '@app/core/services/modal.service';
import { LocalStorageModule } from '@app/angular-2-local-storage';
import { HttpTokenInterceptor } from '@app/core/interceptors/http-token.interceptor';
import { HttpAuthInterceptor } from '@app/core/interceptors/http-auth.interceptor';
import { HttpNotifyInterceptor } from '@app/core/interceptors/http-notify.interceptor';
import { ApiTransferCacheInterceptor } from '@app/core/ssr/api-transfer-cache.interceptor';
import { provideJsactionPrune } from '@app/core/ssr/jsaction-prune.provider';
import { provideLazyImgReaper } from '@app/core/ssr/lazy-img-reaper.provider';
import { provideChunkLoadFallback } from '@app/core/router/chunk-load-fallback.provider';
import { HttpClearParamsInterceptor } from '@app/core/interceptors/http-clear-params.interceptor';
import { TranslocoService } from '@jsverse/transloco';

import { registerLocaleData, isPlatformBrowser } from '@angular/common';
import localeRu from '@app/locales/ru';
import { CustomRouteReuseStrategy } from '@app/core/strategy/custom-reuse.strategy';

registerLocaleData(localeRu);

@NgModule({
    declarations: [AppComponent],
    imports: [
        BrowserModule,
        LayoutsModule,
        AppRoutingModule,
        LocalStorageModule.forRoot({
            prefix: '',
            storageType: 'localStorage'
        }),
    ],
    providers: [
        // OB-7718 фаза 8: zoneless CD (стабильный API v22). CD триггерят
        // markForCheck/signals/AsyncPipe/события шаблонов; таймеры и внешние
        // колбэки обязаны сигналить сами (см. C1-фиксы). SSR-стабильность —
        // PendingTasks (HttpClient ведёт их сам).
        provideZonelessChangeDetection(),
        // OB-7718 фаза 7 (СПАЙК): неразрушающая гидратация SSR-DOM + реплей событий,
        // кликнутых до бутстрапа. Транфер-кэш HTTP пока штатный (проверяем).
        // OB-7718 фаза 7: withIncrementalHydration — @defer(hydrate on viewport)
        // блоки (футер и др. ленивые ниже-фолда) сохраняют SSR-DOM до триггера
        // вместо сноса при первичной гидратации (замена паттерна *intersection)
        provideClientHydration(withEventReplay(), withNoHttpTransferCache(), withIncrementalHydration()),
        // OB-7718 вызывал здесь ɵɵenableIncrementalHydrationRuntime() из инициализатора окружения:
        // до 22.2 bootstrap-листенер срабатывал раньше рендера маршрута и становился no-op.
        // С 22.2 Angular сам делает рукопожатие (requested от листенера + activated из шаблона),
        // а вызов вне шаблона падает: getLView() === null → «reading '9'» (OB-8024).
        // затычка утечки withEventReplay — см. комментарий в jsaction-prune.provider.ts
        provideJsactionPrune(),
        // затычка нативной утечки Chromium: отсоединённые незагруженные lazy-<img>
        // вечно держат DOM-дерево страницы — см. lazy-img-reaper.provider.ts
        provideLazyImgReaper(),
        // чанк ленивого роута не скачался (CDN недоступен у пользователя) —
        // уходим на страницу полной загрузкой, см. chunk-load-fallback.provider.ts
        provideChunkLoadFallback(),
        // OB-7718: единый app-уровневый сабжект параметров баннеров (раньше —
        // отдельный провайдер в каждом page-компоненте; AdsService доставал его
        // хаком через ɵcmp.providersResolver). useFactory: инстанс живёт и
        // умирает вместе с приложением (SSR: без межзапросной утечки).
        // Страница, которая пушит params, реализует интерфейс-маркер AdsParamsSource
        // (AdsService ждёт эмита params перед запросом баннеров).
        { provide: ADS_PARAMS, useFactory: () => new BehaviorSubject(null) },
        { provide: HTTP_INTERCEPTORS, useClass: HttpClearParamsInterceptor, multi: true },
        // OB-7718 фаза 9: 401 token_expired -> single-flight refresh -> retry;
        // строго ПЕРЕД Token, чтобы retry взял свежий токен из куки
        { provide: HTTP_INTERCEPTORS, useClass: HttpAuthInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: HttpTokenInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: HttpNotifyInterceptor, multi: true },
        // transfer-cache API-ответов — строго ПОСЛЕ Notify (':silent:' уже вырезан)
        { provide: HTTP_INTERCEPTORS, useClass: ApiTransferCacheInterceptor, multi: true },
        // ng-bootstrap: общие настройки поповеров (popover.config.ts) и модалок (modal.service.ts)
        provideEnvironmentInitializer(configurePopovers),
        provideEnvironmentInitializer(configureModals),
        { provide: UrlSerializer, useClass: CustomeUrlSerializer },
        { provide: LOCALE_ID, useValue: 'ru' },
//          { provide: RouteReuseStrategy, useClass: CustomRouteReuseStrategy },
        {
            provide: APP_INITIALIZER,
            multi: true,
            useFactory: function (l10n: TranslocoService, meta: Meta, platformId: Object) {
                return function () {
                    if (isPlatformBrowser(platformId)) {
                        l10n.setActiveLang(meta.getTag('name="interface-language"')?.getAttribute('content') || 'ru');
                    }
                };
            },
            deps: [TranslocoService, Meta, PLATFORM_ID]
        }
    ],
    bootstrap: [AppComponent]
})
export class AppModule { }
